<!-- Facet Container -->
<div [class.proposal-card-changed]="changes?.size"
     class="border border-gray-200 rounded-lg">
  <!-- Collapse Header -->
  <div class="flex items-center gap-2 p-4 cursor-pointer" (click)="toggleExpanded()">
    <fa-icon [icon]="facet.expanded ? faChevronDown : faChevronRight" class="text-black w-4"></fa-icon>
    <span class="flex-1 text-sm font-medium text-gray-900">{{ facetLabel }}</span>
    @if (changes?.size) {
      <!-- Auch im eingeklappten Zustand sichtbar: die Feldmarker darunter sieht man erst aufgeklappt.
           ✓/✗ betreffen nur diese eine Facette (Anforderung bzw. Anwendbarkeit). -->
      <span
        class="shrink-0 text-xs font-medium text-green-700">{{ changes?.has('*') && !changes?.get('*')?.before ? 'neu' : 'geändert' }}</span>
      <!-- Klicks auf ✓/✗ sollen die Facette nicht auf-/zuklappen (toggleExpanded am Kopf). -->
      <div (click)="$event.stopPropagation()" class="shrink-0">
        <app-proposal-actions (acceptClicked)="facetAccepted.emit()"
                              (rejectClicked)="facetRejected.emit()"></app-proposal-actions>
      </div>
    }
    <div (click)="$event.stopPropagation()" [bicDropdownTrigger]="facetDropdown"
         class="cursor-pointer px-1 hover:opacity-50">
      <fa-icon [icon]="faEllipsisV" class="text-gray-900"></fa-icon>
    </div>
    <bic-dropdown #facetDropdown [dropdownPlacement]="'bottom-end'">
      <bic-dropdown-element (click)="$event.stopPropagation()" (onClick)="facet.expanded = true; emitChange()">
        <div class="flex flex-row text-sm gap-3 text-gray-700 items-center">
          <fa-icon [icon]="faPen" class="w-4"></fa-icon>
          <span>Bearbeiten</span>
        </div>
      </bic-dropdown-element>
      <bic-dropdown-element (click)="$event.stopPropagation()" (onClick)="duplicate.emit()">
        <div class="flex flex-row text-sm gap-3 text-gray-700 items-center">
          <fa-icon [icon]="faCopy" class="w-4"></fa-icon>
          <span>Duplizieren</span>
        </div>
      </bic-dropdown-element>
      <bic-dropdown-element (click)="$event.stopPropagation()" (onClick)="remove.emit()">
        <div class="flex flex-row text-sm gap-3 text-red-500 items-center">
          <fa-icon [icon]="faTrash" class="w-4"></fa-icon>
          <span>Löschen</span>
        </div>
      </bic-dropdown-element>
    </bic-dropdown>
  </div>

  <!-- Facet Content -->
  @if (facet.expanded) {
    <div class="flex flex-col gap-4 px-4 pb-4">

      <!-- Facettentyp Select (volle Breite) -->
      <div class="flex flex-row gap-4">
        <div class="flex-1">
          <bic-select [value]="facet.type"
                      (valueChange)="onTypeChange($event)"
                      [label]="'Facettentyp'"
                      [placeholder]="'Facettentyp wählen'">
            @for (ft of visibleFacetTypes; track ft.value) {
              <bic-dropdown-element [value]="ft.value">{{ ft.label }}</bic-dropdown-element>
            }
          </bic-select>
        </div>
      </div>

      @switch (facet.type) {

        <!-- ===== ENTITÄT ===== -->
        @case ('entity') {
          <!-- Row: Datentyp + Entität Name -->
          <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.entityName, key: 'entityName', label: 'Entität Name', placeholder: 'z.B. IfcWall', required: true }"></ng-container>
          <!-- Subklassen mitprüfen (#2311): ifctester vergleicht Entitaeten exakt, eine
               Spezifikation auf IfcWall erfasst also keine IfcWallStandardCase. Das Haekchen
               expandiert die Klassen beim Speichern; im Editor bleibt die eingetippte Klasse
               stehen. -->
          <app-change-marker (acceptClicked)="changeAccepted.emit('includeSubtypes')" (rejectClicked)="changeRejected.emit('includeSubtypes')"
                             [change]="$safeNavigationMigration(changes?.get('includeSubtypes'))">
            <label class="flex items-center gap-2 w-fit" data-proposal-group
                   [class.cursor-pointer]="canIncludeSubtypes"
                   [class.cursor-not-allowed]="!canIncludeSubtypes"
                   [tooltip]="includeSubtypesTooltip"
                   [tooltipWidth]="380">
              <input type="checkbox"
                     [checked]="facet.includeSubtypes && canIncludeSubtypes"
                     [disabled]="!canIncludeSubtypes"
                     (change)="toggleIncludeSubtypes()"
                     class="w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500 disabled:opacity-50">
              <span class="text-sm font-medium"
                    [class.text-gray-900]="canIncludeSubtypes"
                    [class.text-gray-400]="!canIncludeSubtypes">Subklassen mitprüfen</span>
              @if (facet.includeSubtypes && canIncludeSubtypes && subtypePreview.length > 0) {
                <span class="text-xs text-gray-500">+{{ subtypePreview.length }} IFC-Klassen</span>
              }
            </label>
          </app-change-marker>
          <!-- Row: Datentyp + Predefined Type -->
          <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.predefinedType, key: 'predefinedType', label: 'Predefined Type (Optional)', placeholder: 'z.B. SOLIDWALL', required: false }"></ng-container>
        }

        <!-- ===== ENTITÄTENGRUPPE ===== -->
        @case ('entityGroup') {
          <div class="flex flex-row gap-4">
            <div class="w-[200px]">
              <bic-select [value]="entityGroupValue"
                          (valueChange)="onEntityGroupChange($event)"
                          [label]="'Entitätengruppe'"
                          [placeholder]="'Gruppe wählen'"
                          [errorCondition]="submitted && isFieldEmpty(facet.entityName)">
                @for (group of IFC_ENTITY_GROUPS; track group.key) {
                  <bic-dropdown-element [value]="group.key">{{ group.label }}</bic-dropdown-element>
                }
              </bic-select>
            </div>
            <div class="flex-1 flex flex-col justify-end">
              @if (selectedEntityGroup) {
                <div class="flex flex-row items-center gap-2 h-[38px]">
                  <bic-badge [tooltip]="entityGroupTooltip(selectedEntityGroup)"
                             [tooltipWidth]="380"
                             class="cursor-help"
                             type="info">{{ selectedEntityGroup.label }}
                  </bic-badge>
                  <span class="text-xs text-gray-500">{{ facet.entityName.enumValues.length }} IFC-Klassen</span>
                </div>
              } @else {
                <span class="text-xs text-gray-500 h-[38px] flex items-center">Bitte eine Gruppe wählen.</span>
              }
            </div>
          </div>
        }

        <!-- ===== ATTRIBUT ===== -->
        @case ('attribute') {
          <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.attributeName, key: 'attributeName', label: 'Attributname', placeholder: 'z.B. Name', required: true }"></ng-container>
          <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.attributeValue, key: 'attributeValue', label: 'Attributwert', placeholder: 'z.B. Basiswand', required: false }"></ng-container>
        }

        <!-- ===== EIGENSCHAFT ===== -->
        @case ('property') {
          <!-- Eigenschaftssatz: '.*' ist der Backend-Default und wird dem Nutzer als leeres Feld angezeigt.
               Ein Marker für beide Zweige; der valueRow im else-Zweig bekommt deshalb keinen eigenen Schlüssel. -->
          <app-change-marker (acceptClicked)="changeAccepted.emit('propertySet')" (rejectClicked)="changeRejected.emit('propertySet')"
                             [change]="$safeNavigationMigration(changes?.get('propertySet'))">
          @if (facet.propertySet.dataType === 'Wert') {
            <div class="flex flex-row gap-4">
              <div class="w-[200px]">
                <bic-select [value]="facet.propertySet.dataType"
                            (valueChange)="onDataTypeChange(facet.propertySet, $event)"
                            [label]="'Datentyp'"
                            [placeholder]="'Datentyp wählen'">
                  @for (dt of DATA_TYPES; track dt) {
                    <bic-dropdown-element [value]="dt">{{ dt }}</bic-dropdown-element>
                  }
                </bic-select>
              </div>
              <div class="flex-1">
                <bic-input [ngModel]="propertySetDisplayValue"
                           (ngModelChange)="onPropertySetChange($event)"
                           [label]="'Eigenschaftssatz (Optional)'"
                           [placeholder]="'z.B. Pset_WallCommon'"
                           [showX]="false"
                           [required]="false">
                </bic-input>
              </div>
            </div>
          } @else {
            <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.propertySet, key: '', label: 'Eigenschaftssatz (Optional)', placeholder: 'z.B. Pset_WallCommon', required: false }"></ng-container>
          }
          </app-change-marker>
          <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.propertyName, key: 'propertyName', label: 'Eigenschaftsname', placeholder: 'z.B. Wandbekleidung', required: true }"></ng-container>
          <!-- Datentyp Eigenschaftswert (special: Select instead of Input) -->
          <app-change-marker (acceptClicked)="changeAccepted.emit('propertyDataType')" (rejectClicked)="changeRejected.emit('propertyDataType')"
                             [change]="$safeNavigationMigration(changes?.get('propertyDataType'))">
          <div class="flex flex-row gap-4">
            <div class="w-[200px]">
              <!-- facet.propertyDataType (rechts) ist ein einfacher IFC-Typ-String (z.B. IfcLabel), kein
                   IDSValueField - hier gibt es kein eigenes "Datentyp"-Konzept, daher fix auf "Wert" und
                   nicht an facet.propertyValue.dataType gekoppelt (das Feld für den Eigenschaftswert selbst
                   steht in der Zeile darunter mit eigenem, unabhängigem Datentyp-Feld). -->
              <bic-select [disabled]="true" [value]="'Wert'" [label]="'Datentyp'">
                <bic-dropdown-element [value]="'Wert'">Wert</bic-dropdown-element>
              </bic-select>
            </div>
          <div class="flex-1 relative">
            <bic-input (blur)="hidePropertyDataTypeDropdown()"
                       (focus)="showPropertyDataTypeDropdown = true"
                       [(ngModel)]="facet.propertyDataType"
                       (ngModelChange)="emitChange()"
                       [isDropdown]="true"
                       [label]="'Datentyp Eigenschaftswert'"
                       [placeholder]="'z.B. IfcLabel'"
                       [showX]="false">
            </bic-input>
            @if (showPropertyDataTypeDropdown
                 && (filteredDataTypeGroups.length > 0 || filteredPropertyDataTypes.length > 0)) {
              <div
                class="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                <!-- Datentypengruppen (#2312) zuerst: "irgendeine Zahl" ist haeufiger gefordert
                     als genau IfcAreaMeasure. Im XML landet das Token der Gruppe. -->
                @if (filteredDataTypeGroups.length > 0) {
                  <div class="px-3 pt-2 pb-1 text-xs font-medium text-gray-500 uppercase">Gruppen</div>
                  @for (group of filteredDataTypeGroups; track group.token) {
                    <div (mousedown)="selectDataTypeGroup(group)"
                         class="px-3 py-2 text-sm text-gray-900 cursor-pointer hover:bg-gray-100">
                      {{ group.label }} <span class="text-gray-400">({{ dataTypeGroupCount(group) }})</span>
                    </div>
                  }
                }
                @if (filteredPropertyDataTypes.length > 0) {
                  @if (filteredDataTypeGroups.length > 0) {
                    <div class="px-3 pt-2 pb-1 text-xs font-medium text-gray-500 uppercase border-t border-gray-100">
                      IFC-Datentypen
                    </div>
                  }
                  @for (pdt of filteredPropertyDataTypes; track pdt) {
                    <div (mousedown)="selectPropertyDataType(pdt)"
                         class="px-3 py-2 text-sm text-gray-900 cursor-pointer hover:bg-gray-100">
                      {{ pdt }}
                    </div>
                  }
                }
            </div>
            }
            @if (selectedDataTypeGroup) {
              <span class="mt-1 inline-block text-xs text-gray-500"
                    [tooltip]="dataTypeGroupTooltipTitle(selectedDataTypeGroup)"
                    [tooltipSubtext]="dataTypeGroupMemberList(selectedDataTypeGroup)"
                    [tooltipWidth]="380">Gruppe „{{ selectedDataTypeGroup.label }}" mit
                {{ dataTypeGroupCount(selectedDataTypeGroup) }} IFC-Datentypen</span>
            }
            </div>
          </div>
          </app-change-marker>
          <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.propertyValue, key: 'propertyValue', label: 'Eigenschaftswert', placeholder: 'z.B. Gipsputz', required: false }"></ng-container>
        }

        <!-- ===== KLASSIFIKATION ===== -->
        @case ('classification') {
          <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.classificationSystem, key: 'classificationSystem', label: 'Klassifizierungssystem', placeholder: 'z.B. DIN 276', required: true }"></ng-container>
          <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.classificationValue, key: 'classificationValue', label: 'Klassifizierungswert', placeholder: 'z.B. 330', required: false }"></ng-container>
        }

        <!-- ===== MATERIAL ===== -->
        @case ('material') {
          <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.materialName, key: 'materialName', label: 'Materialname', placeholder: 'z.B. Stahlbeton', required: true }"></ng-container>
        }

        <!-- ===== TEIL VON ===== -->
        @case ('partOf') {
          <!-- Beziehung (volle Breite) -->
          <div class="flex flex-row gap-4">
            <div class="flex-1">
              <app-change-marker (acceptClicked)="changeAccepted.emit('relation')" (rejectClicked)="changeRejected.emit('relation')"
                                 [change]="$safeNavigationMigration(changes?.get('relation'))">
                <bic-select [(ngModel)]="facet.relation"
                            (ngModelChange)="emitChange()"
                            [label]="'Beziehung'"
                            [placeholder]="'Beziehung wählen'"
                            [required]="true"
                            [errorCondition]="submitted && !facet.relation?.trim()">
                  @for (rel of RELATIONS; track rel) {
                    <bic-dropdown-element [value]="rel">{{ rel }}</bic-dropdown-element>
                  }
                </bic-select>
              </app-change-marker>
            </div>
          </div>
          <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.partOfEntityName, key: 'partOfEntityName', label: 'Entität Name', placeholder: 'z.B. IfcWall', required: true }"></ng-container>
          <ng-container *ngTemplateOutlet="valueRow; context: { field: facet.partOfPredefinedType, key: 'partOfPredefinedType', label: 'Predefined Type (Optional)', placeholder: 'z.B. SOLIDWALL', required: false }"></ng-container>
        }
      }

      <!-- ===== METAINFORMATIONEN (nur bei Anforderungen) ===== -->
      @if (isRequirement) {
        <span class="text-sm font-medium text-gray-900">Metainformationen</span>
        <!-- Row: Beschreibung + Anwendbarkeit -->
        <div class="flex flex-row gap-4">
          <div class="flex-1">
            <app-change-marker (acceptClicked)="changeAccepted.emit('metaDescription')" (rejectClicked)="changeRejected.emit('metaDescription')"
                               [change]="$safeNavigationMigration(changes?.get('metaDescription'))">
              <bic-input [(ngModel)]="facet.metaDescription"
                         (ngModelChange)="emitChange()"
                         [label]="'Beschreibung (Optional)'"
                         [placeholder]="'Beschreibung eingeben'"
                         [showX]="false">
              </bic-input>
            </app-change-marker>
          </div>
          <div class="flex-1">
            <app-change-marker (acceptClicked)="changeAccepted.emit('metaNecessity')" (rejectClicked)="changeRejected.emit('metaNecessity')"
                               [change]="$safeNavigationMigration(changes?.get('metaNecessity'))">
              <bic-select [(ngModel)]="necessityValue"
                          (ngModelChange)="emitChange()"
                          [disabled]="necessityLockedToRequired"
                          [tooltip]="necessityLockedToRequired ? 'Für Entitäts-Anforderungen kann die Anwendbarkeit nicht exportiert werden (IDS-Schema-Beschränkung) - fest auf \'Erforderlich\'.' : undefined"
                          [tooltipWidth]="320"
                          [label]="'Anwendbarkeit'"
                          [placeholder]="'Auswählen'">
                @for (opt of NECESSITY_OPTIONS; track opt.value) {
                  <bic-dropdown-element [value]="opt.value">{{ opt.label }}</bic-dropdown-element>
                }
              </bic-select>
            </app-change-marker>
          </div>
        </div>
        <!-- Row: URI + Anweisung -->
        <div class="flex flex-row gap-4">
          <div class="flex-1">
            <app-change-marker (acceptClicked)="changeAccepted.emit('metaUri')" (rejectClicked)="changeRejected.emit('metaUri')"
                               [change]="$safeNavigationMigration(changes?.get('metaUri'))">
              <bic-input [(ngModel)]="facet.metaUri"
                         (ngModelChange)="emitChange()"
                         [label]="'URI - Uniform Resource Identifier (Optional)'"
                         [placeholder]="'URI eingeben'"
                         [showX]="false">
              </bic-input>
            </app-change-marker>
          </div>
          <div class="flex-1">
            <app-change-marker (acceptClicked)="changeAccepted.emit('metaInstruction')" (rejectClicked)="changeRejected.emit('metaInstruction')"
                               [change]="$safeNavigationMigration(changes?.get('metaInstruction'))">
              <bic-input [(ngModel)]="facet.metaInstruction"
                         (ngModelChange)="emitChange()"
                         [label]="'Anweisung (Optional)'"
                         [placeholder]="'Anweisung eingeben'"
                         [showX]="false">
              </bic-input>
            </app-change-marker>
          </div>
        </div>
      }
    </div>
  }
</div>
<!-- TEMPLATE: Value Row (Datentyp + Wert-Feld)     -->
<!-- Renders differently based on field.dataType    -->
<!-- ============================================== -->
<ng-template #valueRow let-field="field" let-key="key" let-label="label" let-placeholder="placeholder" let-required="required">
  <!-- Ein Marker je Wertfeld (Datentyp + Wert zusammen), Schlüssel = Feldname der Facette. -->
  <app-change-marker (acceptClicked)="changeAccepted.emit(key)" (rejectClicked)="changeRejected.emit(key)"
                     [change]="key ? $safeNavigationMigration(changes?.get(key)) : undefined">
  <div class="flex flex-col gap-4">
  @switch (field.dataType) {

    <!-- === WERT === -->
    @case ('Wert') {
      <div class="flex flex-row gap-4">
        <div class="w-[200px]">
          <bic-select [value]="field.dataType"
                      (valueChange)="onDataTypeChange(field, $event)"
                      [label]="'Datentyp'"
                      [placeholder]="'Datentyp wählen'">
            @for (dt of DATA_TYPES; track dt) {
              <bic-dropdown-element [value]="dt">{{ dt }}</bic-dropdown-element>
            }
          </bic-select>
        </div>
        <div class="flex-1">
          <bic-input [(ngModel)]="field.simpleValue"
                     (ngModelChange)="emitChange()"
                     [label]="label"
                     [placeholder]="placeholder"
                     [showX]="false"
                     [required]="required"
                     [errorCondition]="submitted && required && !field.simpleValue?.trim()">
          </bic-input>
        </div>
      </div>
    }

    <!-- === AUFZÄHLUNG === -->
    @case ('Aufzählung') {
      <div class="flex flex-row gap-4">
        <div class="w-[200px]">
          <bic-select [value]="field.dataType"
                      (valueChange)="onDataTypeChange(field, $event)"
                      [label]="'Datentyp'"
                      [placeholder]="'Datentyp wählen'">
            @for (dt of DATA_TYPES; track dt) {
              <bic-dropdown-element [value]="dt">{{ dt }}</bic-dropdown-element>
            }
          </bic-select>
        </div>
        <div class="flex-1 flex flex-col">
          <!-- Label (gleich wie bic-input) -->
          <label class="block text-sm mb-1 font-medium"
                 [class.text-red]="submitted && required && field.enumValues?.length === 0"
                 [class.text-gray-900]="!(submitted && required && field.enumValues?.length === 0)">{{ label }}</label>
          <!-- Input Container (gleich wie bic-input) -->
          <div class="flex flex-row w-full relative h-[38px]">
            <input type="text"
                   [ngModel]="getEnumInput(field)"
                   (ngModelChange)="setEnumInput(field, $event)"
                   (keydown.enter)="addEnumValue(field, getEnumInput(field))"
                   [placeholder]="placeholder"
                   class="text-sm rounded-lg placeholder-gray-500 block w-full px-2.5 py-[.525rem] bg-gray-50 border text-gray-900 pe-8"
                   [class.border-red]="submitted && required && field.enumValues?.length === 0"
                   [class.border-gray-300]="!(submitted && required && field.enumValues?.length === 0)">
            <button type="button"
                    class="absolute inset-y-0 end-0 flex items-center pe-2.5 text-gray-400 hover:text-gray-600"
                    (click)="addEnumValue(field, getEnumInput(field))">
              <fa-icon [icon]="faPlus" class="text-sm"></fa-icon>
            </button>
          </div>
          <!-- Badges darunter -->
          @if (field.enumValues.length > 0 && showEnumBadges(field)) {
            <div class="flex flex-row flex-wrap gap-2 mt-2">
              @for (val of field.enumValues; track $index) {
                <bic-badge type="info">
                  {{ val }}
                  <fa-icon [icon]="faXmark"
                           class="cursor-pointer hover:text-gray-900"
                           iconRight
                           (click)="removeEnumValue(field, $index)"></fa-icon>
                </bic-badge>
              }
            </div>
          }
        </div>
      </div>
    }

    <!-- === GRENZEN === -->
    @case ('Grenzen') {
      <!-- Minimal/Maximal in EINER Zeile mit gemeinsamer, engerer Spalte (statt zwei eigenen
           Zeilen mit Leer-Spacer): sonst übernimmt der äußere flex-col-gap (für Abstand zwischen
           unabhängigen Feldgruppen gedacht) auch den Abstand zwischen den zwei Hälften desselben
           Feldes - das wirkte je nach Fehlerzustand des oberen Felds uneinheitlich/verspringend.
           items-start, weil die rechte Spalte durch die zwei Inputs höher ist als die linke - ohne
           items-start streckt sich der Datentyp-Select (align-items: stretch, Default) auf diese
           Höhe und lässt darunter einen leeren, unmotiviert wirkenden Bereich stehen. -->
      <div class="flex flex-row gap-4 items-start">
        <div class="w-[200px]">
          <bic-select [value]="field.dataType"
                      (valueChange)="onDataTypeChange(field, $event)"
                      [label]="'Datentyp'"
                      [placeholder]="'Datentyp wählen'">
            @for (dt of DATA_TYPES; track dt) {
              <bic-dropdown-element [value]="dt">{{ dt }}</bic-dropdown-element>
            }
          </bic-select>
        </div>
        <div class="flex-1 flex flex-col gap-2">
          <bic-input [(ngModel)]="field.rangeMin"
                     (ngModelChange)="emitChange()"
                     [type]="'number'"
                     [label]="label + ' Minimal'"
                     [placeholder]="'0'"
                     [showX]="false"
                     [required]="required"
                     [errorCondition]="submitted && required && !field.rangeMin?.trim() && !field.rangeMax?.trim()">
          </bic-input>
          <bic-input [(ngModel)]="field.rangeMax"
                     (ngModelChange)="emitChange()"
                     [type]="'number'"
                     [label]="label + ' Maximal'"
                     [placeholder]="'100'"
                     [showX]="false">
          </bic-input>
        </div>
      </div>
    }

    <!-- === MUSTER === -->
    @case ('Muster') {
      <div class="flex flex-row gap-4">
        <div class="w-[200px]">
          <bic-select [value]="field.dataType"
                      (valueChange)="onDataTypeChange(field, $event)"
                      [label]="'Datentyp'"
                      [placeholder]="'Datentyp wählen'">
            @for (dt of DATA_TYPES; track dt) {
              <bic-dropdown-element [value]="dt">{{ dt }}</bic-dropdown-element>
            }
          </bic-select>
        </div>
        <div class="flex-1">
          <bic-input [(ngModel)]="field.pattern"
                     (ngModelChange)="emitChange()"
                     [label]="label + ' (Regulärer Ausdruck)'"
                     [placeholder]="'z.B. .{5}'"
                     [showX]="false"
                     [required]="required"
                     [errorCondition]="submitted && required && !field.pattern?.trim()">
          </bic-input>
        </div>
      </div>
    }

    <!-- === LÄNGE === -->
    @case ('Länge') {
      <div class="flex flex-row gap-4">
        <div class="w-[200px]">
          <bic-select [value]="field.dataType"
                      (valueChange)="onDataTypeChange(field, $event)"
                      [label]="'Datentyp'"
                      [placeholder]="'Datentyp wählen'">
            @for (dt of DATA_TYPES; track dt) {
              <bic-dropdown-element [value]="dt">{{ dt }}</bic-dropdown-element>
            }
          </bic-select>
        </div>
        <div class="flex-1">
          <bic-input [(ngModel)]="field.lengthValue"
                     (ngModelChange)="emitChange()"
                     [min]="0"
                     [type]="'number'"
                     [label]="label + ' (Exakte Länge)'"
                     [placeholder]="'z.B. 5'"
                     [showX]="false"
                     [required]="required"
                     [errorCondition]="submitted && required && !field.lengthValue?.trim()">
          </bic-input>
        </div>
      </div>
    }

    <!-- === LÄNGE GRENZEN === -->
    @case ('Länge Grenzen') {
      <!-- Minimale/Maximale Länge in EINER Zeile mit gemeinsamer, engerer Spalte (statt zwei
           eigenen Zeilen mit Leer-Spacer) - Begründung wie bei "Grenzen" oben, inkl. items-start. -->
      <div class="flex flex-row gap-4 items-start">
        <div class="w-[200px]">
          <bic-select [value]="field.dataType"
                      (valueChange)="onDataTypeChange(field, $event)"
                      [label]="'Datentyp'"
                      [placeholder]="'Datentyp wählen'">
            @for (dt of DATA_TYPES; track dt) {
              <bic-dropdown-element [value]="dt">{{ dt }}</bic-dropdown-element>
            }
          </bic-select>
        </div>
        <div class="flex-1 flex flex-col gap-2">
          <bic-input [(ngModel)]="field.lengthMin"
                     (ngModelChange)="emitChange()"
                     [min]="0"
                     [type]="'number'"
                     [label]="label + ' Minimale Länge'"
                     [placeholder]="'z.B. 2'"
                     [showX]="false"
                     [required]="required"
                     [errorCondition]="submitted && required && !field.lengthMin?.trim() && !field.lengthMax?.trim()">
          </bic-input>
          <bic-input [(ngModel)]="field.lengthMax"
                     (ngModelChange)="emitChange()"
                     [min]="0"
                     [type]="'number'"
                     [label]="label + ' Maximale Länge'"
                     [placeholder]="'z.B. 10'"
                     [showX]="false">
          </bic-input>
        </div>
      </div>
    }
  }
  </div>
  </app-change-marker>
</ng-template>
